import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import HeadMeta from '../components/HeadMeta';
import ScrollReveal from '../components/ScrollReveal';
import { useScrollZoom } from '../hooks/useScrollZoom';

export default function PlanTrip() {
  const { lang, t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('families');
  const heroZoom = useScrollZoom({ baseScale: 1.1, maxScale: 1.4, zoomSpeed: 0.0006 });

  const categories = [
    {
      id: 'families',
      title: t('planTripPage.families'),
      desc: t('planTripPage.familiesDesc'),
      icon: 'fa-solid fa-people-roof',
      features: t('planTripPage.familiesFeatures') || []
    },
    {
      id: 'couples',
      title: t('planTripPage.couples'),
      desc: t('planTripPage.couplesDesc'),
      icon: 'fa-solid fa-heart-pulse',
      features: t('planTripPage.couplesFeatures') || []
    },
    {
      id: 'groups',
      title: t('planTripPage.groups'),
      desc: t('planTripPage.groupsDesc'),
      icon: 'fa-solid fa-users-gear',
      features: t('planTripPage.groupsFeatures') || []
    },
    {
      id: 'solo',
      title: t('planTripPage.solo'),
      desc: t('planTripPage.soloDesc'),
      icon: 'fa-solid fa-person-hiking',
      features: t('planTripPage.soloFeatures') || []
    }
  ];

  return (
    <>
      <HeadMeta 
        title={t('planTripPage.title') || 'Planlæg din rejse'} 
        description={t('planTripPage.subtitle') || 'Skræddersyet ferieplanlægning i Hurghada for familier, par, grupper og individuelle rejsende med Captain Hurghada Tours.'}
      />

      {/* Hero */}
      <section className="pt-28 sm:pt-36 lg:pt-40 pb-16 bg-slate-950 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/orangebay.jpeg"
            alt="Planlæg rejse Hurghada"
            style={{
              transform: `scale(${heroZoom.scale}) translate3d(0, ${heroZoom.translateY}px, 0)`,
              transition: 'transform 0.1s cubic-bezier(0.1, 1, 0.1, 1)',
              willChange: 'transform',
            }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pointer-events-none" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal variant="up" duration={800}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 uppercase">
              {t('planTripPage.title')}
            </h1>
          </ScrollReveal>

          <ScrollReveal variant="up" delay={200} duration={800}>
            <p className="text-sm sm:text-base text-slate-300 max-w-3xl mx-auto leading-relaxed">
              {t('planTripPage.intro')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Interactive Category Selector */}
      <section className="py-20 bg-[#FAF9F5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3 uppercase tracking-tight">
              {t('planTripPage.optionsTitle')}
            </h2>
          </div>

          {/* Category Tabs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto mb-8">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`p-6 rounded-2xl text-center border transition-all duration-300 flex flex-col items-center justify-center gap-3 cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-cyan-600 text-white border-cyan-600 shadow-md font-bold'
                    : 'bg-white text-slate-800 border-slate-200 shadow-sm hover:border-cyan-500/50 hover:shadow-md'
                }`}
              >
                <i className={`${cat.icon} text-2xl ${activeCategory === cat.id ? 'text-white' : 'text-cyan-600'}`} aria-hidden="true" />
                <span className="text-sm font-bold uppercase tracking-wider">{cat.title}</span>
              </button>
            ))}
          </div>

          {/* Selected Category Feature Card */}
          {categories.map((cat) => {
            if (cat.id !== activeCategory) return null;
            return (
              <div 
                key={cat.id}
                className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-100 shadow-xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center max-w-5xl mx-auto animate-in fade-in duration-300"
              >
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 text-cyan-600 text-xs font-bold uppercase tracking-wider">
                    <i className={cat.icon} aria-hidden="true" />
                    <span>{cat.title}</span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-snug uppercase tracking-tight">
                    {cat.desc}
                  </h3>

                  <ul className="space-y-3.5 text-sm text-slate-700">
                    {cat.features.map((feat, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <i className="fa-solid fa-check text-cyan-600 font-bold mt-1 text-sm shrink-0" aria-hidden="true" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4">
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm tracking-wide transition-all shadow-md"
                    >
                      <span>{t('planTripPage.ctaCustom')}</span>
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                    </Link>
                  </div>
                </div>

                <div className="rounded-2xl overflow-hidden shadow-md aspect-[4/3] bg-slate-100">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/family-beach-resort.jpg"
                    alt={cat.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            );
          })}

        </div>
      </section>
    </>
  );
}
